import React, { useEffect, useMemo, useState } from 'react';
import SuperadminLayout from '../../components/SuperadminLayout';
import { listTirages, createTirage, updateTirage, deleteTirage } from '../../api/tirages';
import { listCompanies } from '../../api/superadmin';
import './SuperadminTiragesPage.css';

const emptyForm = { name: '', start_time: '', end_time: '', is_active: true, company: '' };

// Gestion COMPLETE du catalogue de tirages par le superadmin (demande explicite
// 2026-09-22) — mirroir React de ce que Django Admin permet deja, meme API
// (TirageViewSet) : `company` vide = tirage GLOBAL (visible par toutes les
// companies, catalogue historique) ; renseigne = tirage PRIVE pour CETTE
// company precise (server-side, jamais contournable par une company — cf.
// TirageViewSet.perform_create/perform_update).
const SuperadminTiragesPage = () => {
  const [tirages, setTirages] = useState([]);
  const [companies, setCompanies] = useState([]);
  const [loading, setLoading] = useState(true);
  const [form, setForm] = useState(emptyForm);
  const [creating, setCreating] = useState(false);
  const [error, setError] = useState('');

  const [editId, setEditId] = useState(null);
  const [editForm, setEditForm] = useState(emptyForm);
  const [busyId, setBusyId] = useState(null);

  const load = () => {
    setLoading(true);
    listTirages()
      .then(setTirages)
      .catch(() => setError('Impossible de charger les tirages.'))
      .finally(() => setLoading(false));
  };

  useEffect(load, []);
  useEffect(() => { listCompanies().then(setCompanies).catch(() => {}); }, []);

  const companyName = (id) => companies.find((c) => c.id === id)?.name || `#${id}`;

  // Code automatique (demande explicite 2026-09-22) : continue sur le plus
  // grand code du catalogue GLOBAL (pas les tirages prives des companies,
  // qui peuvent avoir des codes sans rapport) + 1 — plus besoin de le taper.
  const nextCode = useMemo(() => {
    const globalCodes = tirages.filter((t) => t.is_global).map((t) => t.code);
    return globalCodes.length ? Math.max(...globalCodes) + 1 : 1;
  }, [tirages]);

  const updateField = (field, value) => setForm((prev) => ({ ...prev, [field]: value }));
  const updateEditField = (field, value) => setEditForm((prev) => ({ ...prev, [field]: value }));

  const toPayload = (f, code) => ({
    code,
    name: f.name,
    start_time: f.start_time || null,
    end_time: f.end_time || null,
    is_active: f.is_active,
    company: f.company || null,
  });

  const handleCreate = async (e) => {
    e.preventDefault();
    setCreating(true);
    setError('');
    try {
      const created = await createTirage(toPayload(form, nextCode));
      setTirages((prev) => [...prev, created].sort((a, b) => a.code - b.code));
      setForm(emptyForm);
    } catch (err) {
      setError('Creation impossible.');
    } finally {
      setCreating(false);
    }
  };

  const startEdit = (t) => {
    setEditId(t.id);
    setEditForm({
      code: String(t.code),
      name: t.name,
      start_time: t.start_time || '',
      end_time: t.end_time || '',
      is_active: t.is_active,
      company: t.company || '',
    });
    setError('');
  };

  const saveEdit = async (id) => {
    setBusyId(id);
    setError('');
    try {
      const updated = await updateTirage(id, toPayload(editForm, Number(editForm.code)));
      setTirages((prev) => prev.map((t) => (t.id === id ? updated : t)).sort((a, b) => a.code - b.code));
      setEditId(null);
    } catch (err) {
      setError('Modification impossible.');
    } finally {
      setBusyId(null);
    }
  };

  const removeTirage = async (t) => {
    if (!window.confirm(`Supprimer le tirage "${t.code}-${t.name}" ?`)) return;
    setBusyId(t.id);
    setError('');
    try {
      await deleteTirage(t.id);
      setTirages((prev) => prev.filter((x) => x.id !== t.id));
    } catch (err) {
      // Message du serveur (ex. tirage qui a deja des fiches -> refuse).
      let detail = '';
      try { detail = JSON.parse(err.message)?.detail || ''; } catch { /* message brut */ }
      setError(detail || 'Suppression impossible.');
    } finally {
      setBusyId(null);
    }
  };

  return (
    <SuperadminLayout>
      <div className="stp-header">
        <h2 className="stp-title">Tirages</h2>
      </div>

      <p className="stp-note">
        Catalogue complet, toutes companies confondues. <strong>Global</strong> (company
        vide) = visible par toutes les companies — c'est le catalogue historique
        (NY MIDI, etc.). <strong>Privé</strong> = visible uniquement par la company choisie ;
        chaque company peut aussi créer les siens elle-même depuis Paramètres &gt; Mes Tirages.
      </p>

      {error && <p className="stp-error">{error}</p>}

      {loading ? (
        <p>Chargement...</p>
      ) : (
        <table className="stp-table">
          <thead>
            <tr>
              <th>Code</th>
              <th>Nom</th>
              <th>Heure début</th>
              <th>Heure fin</th>
              <th>Actif</th>
              <th>Portée</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {tirages.map((t) => (
              editId === t.id ? (
                <tr key={t.id}>
                  <td>
                    <input
                      className="stp-input stp-input-inline stp-input-code"
                      type="number"
                      value={editForm.code}
                      onChange={(e) => updateEditField('code', e.target.value)}
                    />
                  </td>
                  <td>
                    <input
                      className="stp-input stp-input-inline"
                      value={editForm.name}
                      onChange={(e) => updateEditField('name', e.target.value)}
                    />
                  </td>
                  <td>
                    <input
                      className="stp-input stp-input-inline"
                      type="time"
                      value={editForm.start_time}
                      onChange={(e) => updateEditField('start_time', e.target.value)}
                    />
                  </td>
                  <td>
                    <input
                      className="stp-input stp-input-inline"
                      type="time"
                      value={editForm.end_time}
                      onChange={(e) => updateEditField('end_time', e.target.value)}
                    />
                  </td>
                  <td>
                    <input
                      type="checkbox"
                      checked={editForm.is_active}
                      onChange={(e) => updateEditField('is_active', e.target.checked)}
                    />
                  </td>
                  <td>
                    <select
                      className="stp-input stp-input-inline"
                      value={editForm.company}
                      onChange={(e) => updateEditField('company', e.target.value)}
                    >
                      <option value="">— Global —</option>
                      {companies.map((c) => (
                        <option key={c.id} value={c.id}>{c.name}</option>
                      ))}
                    </select>
                  </td>
                  <td className="stp-actions">
                    <button type="button" onClick={() => saveEdit(t.id)} disabled={busyId === t.id}>
                      Enregistrer
                    </button>
                    <button type="button" onClick={() => setEditId(null)}>Annuler</button>
                  </td>
                </tr>
              ) : (
                <tr key={t.id}>
                  <td>{t.code}</td>
                  <td>{t.name}</td>
                  <td>{t.start_time || '—'}</td>
                  <td>{t.end_time || '—'}</td>
                  <td>{t.is_active ? 'Oui' : 'Non'}</td>
                  <td>
                    {t.is_global ? (
                      <span className="stp-badge stp-badge-global">Global</span>
                    ) : (
                      <span className="stp-badge stp-badge-prive">{companyName(t.company)}</span>
                    )}
                  </td>
                  <td className="stp-actions">
                    <button type="button" onClick={() => startEdit(t)}>Modifier</button>
                    <button
                      type="button"
                      className="stp-del"
                      onClick={() => removeTirage(t)}
                      disabled={busyId === t.id}
                    >
                      Supprimer
                    </button>
                  </td>
                </tr>
              )
            ))}
            {tirages.length === 0 && (
              <tr><td colSpan={7} className="stp-empty">Aucun tirage.</td></tr>
            )}
          </tbody>
        </table>
      )}

      <form className="stp-form" onSubmit={handleCreate}>
        <div className="stp-form-title">Ajouter un tirage</div>
        <div className="stp-row">
          <label className="stp-label">Code</label>
          <input className="stp-input stp-input-code" type="number" value={nextCode} disabled />
          <span className="stp-hint">automatique</span>
        </div>
        <div className="stp-row">
          <label className="stp-label">Nom</label>
          <input
            className="stp-input"
            type="text"
            placeholder="ex: NY MIDI"
            value={form.name}
            onChange={(e) => updateField('name', e.target.value)}
            required
          />
        </div>
        <div className="stp-row">
          <label className="stp-label">Heure début</label>
          <input
            className="stp-input"
            type="time"
            value={form.start_time}
            onChange={(e) => updateField('start_time', e.target.value)}
          />
        </div>
        <div className="stp-row">
          <label className="stp-label">Heure fin</label>
          <input
            className="stp-input"
            type="time"
            value={form.end_time}
            onChange={(e) => updateField('end_time', e.target.value)}
          />
        </div>
        <div className="stp-row">
          <label className="stp-label">Company</label>
          <select
            className="stp-input"
            value={form.company}
            onChange={(e) => updateField('company', e.target.value)}
          >
            <option value="">— Global (toutes companies) —</option>
            {companies.map((c) => (
              <option key={c.id} value={c.id}>{c.name}</option>
            ))}
          </select>
        </div>
        <label className="stp-checkbox-label">
          <input
            type="checkbox"
            checked={form.is_active}
            onChange={(e) => updateField('is_active', e.target.checked)}
          />
          Actif
        </label>
        <button type="submit" className="stp-add-btn" disabled={creating}>
          {creating ? 'Création...' : '+ Ajouter un tirage'}
        </button>
      </form>
    </SuperadminLayout>
  );
};

export default SuperadminTiragesPage;
